import { Controller } from "@hotwired/stimulus"
import { useDebounce } from "stimulus-use"
import { get, post } from "@rails/request.js"

// This stimulus controller manages the sidebar overlay
// Connects to data-controller="sidebar"
export default class extends Controller {
  static values = { path: String }
  static debounces = ["submitWithDebounce", "refreshDependentFilters"]
  static outlets = ["filterbar"]
  static targets = [
    "expandAllButton",
    "expandIcon",
    "collapseIcon",
    "totalCount",
  ]

  connect() {
    useDebounce(this, { wait: 500 })
    this.allExpanded = false
    this.isOpen = false
    this.show()
    this.updateTotalCount()
  }

  disconnect() {
    // If the sidebar is open when navigating to a different page, close it to prevent issues
    this.hide()
  }

  toggle() {
    if (this.isOpen) {
      this.hide()
    } else {
      this.show()
    }
  }

  async refreshDependentFilters(event) {
    try {
      const select = event.target
      const selectedIds = Array.from(select.selectedOptions).map((o) => o.value)
      const sidebarPath = this.pathValue.replace("/reset", "/sidebar")
      const url = new URL(sidebarPath, window.location.origin)
      selectedIds.forEach((id) => url.searchParams.append("organization[]", id))
      url.searchParams.set("dependent_filters", "true")

      await get(url.toString(), { responseKind: "turbo-stream" })
    } catch (error) {
      console.log("Error:", error)
    }
  }

  show() {
    this.isOpen = true
    this.element.classList.remove("translate-x-full")
    this.element.classList.remove("pointer-events-none")
    this.element.classList.add("pointer-events-auto")
  }

  hide() {
    this.isOpen = false
    this.element.classList.add("translate-x-full")
    this.element.classList.add("pointer-events-none")
    this.element.classList.remove("pointer-events-auto")
  }

  submit() {
    this.filterbarOutlet.submit()
  }

  submitWithDebounce() {
    this.filterbarOutlet.submit()
  }

  async reset() {
    try {
      await post(this.pathValue, { responseKind: "turbo-stream" })

      // Re-submit with only the search query so the server can restore default filters.
      const filterbarForm = document.getElementById("filterbarForm")
      if (!filterbarForm) return

      const externalFields = document.querySelectorAll(
        '[form="filterbarForm"][name]:not([name="query"])',
      )
      const internalFields = filterbarForm.querySelectorAll(
        '[name]:not([name="query"])',
      )

      // Disable non-query inputs for this submit only, including hidden submitted.
      const disabledFields = new Set([...externalFields, ...internalFields])
      disabledFields.forEach((element) => {
        element.disabled = true
      })

      this.submit()
    } catch (error) {
      console.log("Error:", error)
    }
  }

  toggleExpandAll() {
    const filterSections = this.element.querySelectorAll(
      '[data-controller*="toggle-visibility"]',
    )

    const shouldExpand = !this.allExpanded

    filterSections.forEach((section) => {
      const isExpanded =
        section.getAttribute("data-toggle-visibility-expanded-value") === "true"

      if (isExpanded !== shouldExpand) {
        const controller =
          this.application.getControllerForElementAndIdentifier(
            section,
            "toggle-visibility",
          )
        if (controller) {
          controller.toggle()
        }
      }
    })

    this.expandIconTarget.classList.toggle("hidden")
    this.collapseIconTarget.classList.toggle("hidden")

    this.allExpanded = !this.allExpanded
  }

  updateTotalCount() {
    if (!this.hasTotalCountTarget) return

    const selectElements = this.element.querySelectorAll(
      '[data-filter-target="select"]',
    )
    const inputElements = this.element.querySelectorAll(
      '[data-filter-target="input"]',
    )

    let totalCount = 0

    selectElements.forEach((select) => {
      totalCount += select.selectedOptions.length
    })

    inputElements.forEach((input) => {
      if (input.value !== "") {
        totalCount += 1
      }
    })

    if (totalCount > 0) {
      this.totalCountTarget.textContent = ` (${totalCount})`
    } else {
      this.totalCountTarget.textContent = ""
    }
  }
}
